/**
 * Property Inspection Booking Page
 * Inspection 예약 페이지
 */

import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useQuery } from "@tanstack/react-query";
import { Property } from "@shared/schemas/properties";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import { useState } from "react";
import { useLocation, useRoute } from "wouter";

export default function PropertyInspection() {
  const [match, params] = useRoute<{ id: string }>("/properties/:id/inspection");
  const [, setLocation] = useLocation();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!match || !params) {
    return (
      <PageContainer>
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">유효하지 않은 매물 ID입니다</p>
        </Card>
      </PageContainer>
    );
  }

  const propertyId = params.id;

  const { data: propertyData } = useQuery<any>({
    queryKey: ["/api/properties", propertyId],
    queryFn: async () => {
      const res = await fetch(`/api/properties/${propertyId}`);
      if (!res.ok) throw new Error("Failed to fetch property");
      const json = await res.json();
      return json.ok && json.data ? json.data : json;
    },
  });

  const property: Property | null = propertyData || null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // TODO: API 엔드포인트 구현
      const response = await fetch(`/api/properties/${propertyId}/inspection`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date: selectedDate,
          time: selectedTime,
          name,
          email,
          phone,
          message,
        }),
      });

      if (!response.ok) throw new Error("Failed to book inspection");

      alert("Inspection 예약이 완료되었습니다.");
      setLocation(`/properties/${propertyId}`);
    } catch (error) {
      console.error("Error booking inspection:", error);
      alert("예약에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!property) {
    return (
      <PageContainer>
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">매물 정보를 불러오는 중...</p>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Button
        variant="ghost"
        onClick={() => setLocation(`/properties/${propertyId}`)}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        돌아가기
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Inspection 예약
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6 p-4 bg-muted rounded-lg">
            <h3 className="font-semibold mb-2">{property.address || `${property.suburb || property.city}, ${property.state}`}</h3>
            <p className="text-sm text-muted-foreground">
              {property.bedrooms}bed · {property.bathrooms}bath · {property.parkingSpaces}park
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">예약 날짜 <span className="text-red-500">*</span></Label>
                <Input
                  id="date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  required
                  min={new Date().toISOString().split("T")[0]}
                />
              </div>
              <div>
                <Label htmlFor="time">예약 시간 <span className="text-red-500">*</span></Label>
                <Input
                  id="time"
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  required
                />
              </div>
            </div>

            <div>
              <Label htmlFor="name">이름 <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="email">이메일 <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <Label htmlFor="phone">전화번호</Label>
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="message">메시지 (선택사항)</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="추가 요청사항이나 질문이 있으시면 입력해주세요"
                rows={4}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "예약 중..." : "예약하기"}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>참고사항:</strong> 예약 확인 후 에이전트가 연락드립니다. 예약 시간에 늦지 않도록 주의해주세요.
            </p>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

